/// <amd-module name="VSSUI/Components/MessageBar/MessageBar" />
import * as React from "react";
import { ScreenBreakpoints, ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { Breakpoint } from "VSSUI/Breakpoint";
import { Button, IButtonProps } from "VSSUI/Button";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { Icon, IIconProps } from "VSSUI/Icon";
import * as Resources from "VSSUI/Resources.MessageBar";
import { css } from "VSSUI/Util";
import { IMessageBarProps, MessageBarSeverity } from "./MessageBar.Props";

export const MessageBar = (props: IMessageBarProps & Readonly<{ children?: React.ReactNode }>) => {
    const { buttonProps, className, messageClassName, iconProps, onDismiss, severity = MessageBarSeverity.Info } = props;
    const severityProps = MessageBarSeverityInternal[severity];
    const severityClassName = severityProps.className;
    const renderIconProps = iconProps || severityProps.defaultIconProps;

    const screenSize = React.useContext(ScreenContext);
    const [mobile, setMobile] = React.useState(screenSize.size.value === ScreenSize.xsmall);
    const dismissButtonLabel = format(Resources.DismissButtonLabel, getSeverityString(severity));

    return (
        <div className={css(className, "bolt-messagebar", severityClassName, mobile && "mobile")}>
            <Breakpoint breakpoints={[1, ScreenBreakpoints.small]} onBreakpoint={index => setMobile(index === 0)} />
            <FocusZone direction={FocusZoneDirection.Horizontal}>
                <div className={css("bolt-messagebar-content flex-grow", mobile && screenSize.size.value === ScreenSize.xsmall ? "flex-column" : "flex-row")}>
                    <div className={css("flex-row", messageClassName)}>
                        <div className="bolt-messagebar-icons flex-row">
                            <Icon {...renderIconProps} className={css(renderIconProps.className, "bolt-messagebar-icon medium")} />
                        </div>
                        <div
                            className="bolt-messagebar-message flex-row flex-wrap flex-grow flex-shrink flex-center body-m word-break"
                            role={severity === MessageBarSeverity.Error || severity === MessageBarSeverity.Warning ? "alert" : undefined}
                        >
                            {props.children}
                        </div>
                        {onDismiss && mobile && (
                            <Button
                                ariaLabel={dismissButtonLabel}
                                className="bolt-messagebar-close-button relative flex-self-start"
                                iconProps={{ iconName: "Cancel" }}
                                key="closeButton"
                                onClick={onDismiss}
                                subtle={true}
                            />
                        )}
                    </div>
                    {((onDismiss && !mobile) || (buttonProps && buttonProps.length > 0)) && (
                        <div className="bolt-messagebar-buttons flex-noshrink flex-row flex-center flex-self-stretch">
                            {buttonProps && buttonProps.map((value: IButtonProps, index: number) => <Button key={index} {...value} />)}
                            {onDismiss && !mobile && (
                                <Button
                                    ariaLabel={dismissButtonLabel}
                                    className="bolt-messagebar-close-button"
                                    iconProps={{ iconName: "Cancel" }}
                                    key="closeButton"
                                    onClick={onDismiss}
                                    subtle={true}
                                />
                            )}
                        </div>
                    )}
                </div>
            </FocusZone>
        </div>
    );
};

function getSeverityString(severity: MessageBarSeverity): string {
    switch (severity) {
        case MessageBarSeverity.Info:
            return Resources.Info;
        case MessageBarSeverity.Error:
            return Resources.Error;
        case MessageBarSeverity.Success:
            return Resources.Success;
        case MessageBarSeverity.Warning:
            return Resources.Warning;
    }
    return "";
}

// Set the function component's name so that Enzyme can recognize it
MessageBar.displayName = "MessageBar";

interface ISeverityData {
    defaultIconProps: IIconProps;
    className: string;
}

/**
 * Internal backing struct maps MessageBarSeverity to the necessary data
 */
const MessageBarSeverityInternal: { [severityName: string]: ISeverityData } = {
    Info: {
        className: "severity-info",
        defaultIconProps: {
            iconName: "Info"
        }
    },
    Success: {
        className: "severity-success",
        defaultIconProps: { iconName: "Completed" }
    },
    Warning: {
        className: "severity-warning",
        defaultIconProps: { iconName: "Warning" }
    },
    Error: {
        className: "severity-error",
        defaultIconProps: { iconName: "ErrorBadge" }
    }
};
